import { flowSlots } from './flow.js';
import { deriveGraph } from './graph.js';
/**
 * `stay` returns null: it is not an exit at all, and listing it would put a row on the diagram for
 * a rung that keeps the student exactly where they are.
 */
function targetOf(action, fromId, nextOf) {
    switch (action.type) {
        case 'stay': return null;
        case 'end': return { kind: 'end' };
        // A positional next with nothing after it IS an ending — see `nextOf`, which is the one
        // definition of where "next" lands.
        case 'next': {
            const to = nextOf[fromId] ?? null;
            return to ? { kind: 'task', taskId: to } : { kind: 'end' };
        }
        case 'goto': return action.targetTaskId ? { kind: 'task', taskId: action.targetTaskId } : { kind: 'unset' };
    }
}
/** Every exit of one task, in the order `flowSlots` censuses them. */
function exitsOf(task, nextOf) {
    if (!task.flow)
        return [];
    const out = [];
    for (const { slot, action } of flowSlots(task.flow)) {
        const target = targetOf(action, task.id, nextOf);
        if (target)
            out.push({ ownerTaskId: task.id, slot, target });
    }
    return out;
}
export function taskConnections(tasks, taskId) {
    // Copied because `deriveGraph` takes a mutable array and this view promises not to mutate.
    const { nextOf } = deriveGraph([...tasks]);
    const arrivesFrom = [];
    let goesTo = [];
    for (const task of tasks) {
        const exits = exitsOf(task, nextOf);
        if (task.id === taskId)
            goesTo = exits;
        // A task's own slot that loops back to itself is a real arrival and belongs in both lists:
        // the student does leave and come back, and a revisit is exactly what the arrival policy is
        // about. Filtering self out here would hide the one route most likely to surprise.
        for (const link of exits) {
            if (link.target.kind === 'task' && link.target.taskId === taskId)
                arrivesFrom.push(link);
        }
    }
    return { arrivesFrom, goesTo };
}
/**
 * WHICH ARRIVAL ACTUALLY HAPPENED, read back off the attempt log.
 *
 * Not stored anywhere: an arrival is the far end of some other task's exit, and what the log keeps
 * is the exit. So this walks backwards for the most recent attempt that routed here and reports it
 * as the link it was — which also means a route and the arrival that used it are the same object,
 * and the panel can mark one against the other by identity rather than by re-deriving a match.
 *
 * The walk is backwards because a student may arrive here more than once; the question is always
 * "how did I get here THIS time".
 */
export function arrivalInto(tasks, taskId, attempts) {
    const { nextOf } = deriveGraph([...tasks]);
    for (let i = attempts.length - 1; i >= 0; i--) {
        const attempt = attempts[i];
        // A task's own attempt can route back to itself, and that IS an arrival — see the self-loop
        // case in `taskConnections`. So this deliberately does not skip `attempt.taskId === taskId`.
        const target = targetOf(attempt.outcome, attempt.taskId, nextOf);
        if (target?.kind !== 'task' || target.taskId !== taskId)
            continue;
        // An attempt recorded before slots existed has none. It still routed here, but there is no
        // rung to point at, so it cannot mark a row and is reported as no arrival rather than as a
        // guess at which one.
        return attempt.slot ? { ownerTaskId: attempt.taskId, slot: attempt.slot, target } : null;
    }
    return null;
}
/**
 * WHICH RUNG THIS TASK LAST FIRED, exit or not.
 *
 * It was `firedExitOf` and returned null for a stay, which cost the readers that draw the rung
 * itself. A submission that keeps the student here HAS fired something -- "No answer", a
 * special event, the default -- and the attempt records exactly which, in `slot`. Reporting
 * that as "nothing fired" left the Preview diagram unable to tell a result from a forecast on
 * every stay: the rung it drew was the one a draft answer PREDICTED, outlined, when the student
 * had actually submitted and landed on it.
 *
 * A caller that wants exits only does not need a second walk: a stay's slot is not among
 * `taskConnections(...).goesTo`, so matching against that list excludes it for free.
 *
 * An attempt recorded before slots existed has none, and is reported as nothing fired rather
 * than as a guess at which rung it was.
 */
export function firedSlotOf(taskId, attempts) {
    for (let i = attempts.length - 1; i >= 0; i--) {
        const attempt = attempts[i];
        if (attempt.taskId !== taskId)
            continue;
        return attempt.slot ?? null;
    }
    return null;
}
//# sourceMappingURL=task-connections.js.map